import {
  ACTIVATION_ORDER,
  activationColor,
  activationLabel,
  REASON_PRIORITY,
  colorForSupport,
  resolveSupportLabel,
} from '../../lib/classification';
import { formatBytes } from '../../lib/formatBytes';
import type { FullHierarchyNode } from '../../lib/hierarchy/buildFullHierarchy';
import { consumeIfPanelOutsideCloseGesture } from '~/shared/utils/panelOutsideCloseGesture';
import { ttRow } from '../../lib/tooltipHtml';
import {
  explorationCanvasEmptyStateHtml,
  explorationCanvasEmptyVolumesMessage,
} from '../explorationCanvasChrome';
import type { ExplorationVolume, ViewMode } from '../../types/exploration';

const TREE_TYPE_LABEL: Record<string, string> = {
  account: 'Account',
  cluster: 'Cluster',
  nodeGroup: 'Node group',
  instance: 'Instance',
  ec2: 'EC2',
  unattached: 'Unattached',
  volume: 'Volume',
  region: 'Region',
  tag: 'Tag',
};

const TREE_CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 15 12 9 18 15"/></svg>';

export interface TreeRenderOptions {
  view: ViewMode;
  secondary: import('../../types/exploration').ExplorationSecondary;
  tagKey: string;
  volumes: ExplorationVolume[];
  tree: FullHierarchyNode;
  onSelectVolume: (volume: ExplorationVolume) => void;
  onSelectInstance: (instanceId: string, vols: ExplorationVolume[]) => void;
  showTooltip: (html: string, x: number, y: number) => void;
  hideTooltip: () => void;
}

export function renderTreeView(container: HTMLElement, options: TreeRenderOptions): () => void {
  container.innerHTML = '';
  container.classList.add('tree-canvas');

  const { volumes, view } = options;
  if (!volumes.length) {
    options.hideTooltip();
    container.innerHTML = explorationCanvasEmptyStateHtml(explorationCanvasEmptyVolumesMessage);
    return () => {};
  }

  const tree = options.tree;
  function shouldExpand(node: FullHierarchyNode) {
    if (node.children && node.children.length === 1) return true;
    return false;
  }

  const palette =
    view === 'activation'
      ? {
          field: 'activationStatus' as const,
          order: ACTIVATION_ORDER,
          label: activationLabel,
          color: activationColor,
        }
      : {
          field: 'supportStatus' as const,
          order: ['supported', ...REASON_PRIORITY],
          label: (k: string) => resolveSupportLabel(k || ''),
          color: (k: string) => colorForSupport(k),
        };

  function collectVolumes(node: FullHierarchyNode): ExplorationVolume[] {
    if (node.type === 'volume' && node.volume) return [node.volume];
    if (!node.children) {
      return node._volumes?.length ? node._volumes : [];
    }
    const out: ExplorationVolume[] = [];
    node.children.forEach((c) => out.push(...collectVolumes(c)));
    if (out.length === 0 && node._volumes?.length) return node._volumes;
    return out;
  }

  const listeners: Array<{ el: Element; type: string; fn: EventListener }> = [];

  function addListener(el: Element, type: string, fn: EventListener) {
    el.addEventListener(type, fn);
    listeners.push({ el, type, fn });
  }

  function buildStatusBar(node: FullHierarchyNode) {
    const vols = collectVolumes(node);
    if (vols.length === 0) return null;
    const counts = new Map<string, number>();
    const storage = new Map<string, number>();
    vols.forEach((v) => {
      const k = v[palette.field];
      counts.set(k, (counts.get(k) || 0) + 1);
      storage.set(k, (storage.get(k) || 0) + (v.size || 0));
    });
    const bar = document.createElement('div');
    bar.className = 'tree-statusbar';
    const ordered = palette.order.filter((k) => counts.has(k));
    counts.forEach((_, k) => {
      if (!ordered.includes(k)) ordered.push(k);
    });
    ordered.forEach((k) => {
      const n = counts.get(k)!;
      const s = storage.get(k)!;
      const seg = document.createElement('div');
      seg.className = 'seg';
      seg.style.background = palette.color(k);
      seg.style.flex = `${Math.max(s, 0.0001)} 0 0`;
      addListener(seg, 'mouseenter', (e) => {
        options.showTooltip(
          `<div class="tt-title">${palette.label(k)}</div>` +
            ttRow('Volumes', `${n.toLocaleString()} ${n === 1 ? 'volume' : 'volumes'}`) +
            ttRow('Storage', formatBytes(s)),
          (e as MouseEvent).clientX,
          (e as MouseEvent).clientY
        );
      });
      addListener(seg, 'mouseleave', () => options.hideTooltip());
      bar.appendChild(seg);
    });
    return bar;
  }

  function renderNode(node: FullHierarchyNode, depth: number, parentEl: HTMLElement) {
    const wrap = document.createElement('div');
    wrap.className = 'tree-node';

    const row = document.createElement('div');
    row.className = 'tree-row';

    const hasKids = node.children && node.children.length > 0 && node.type !== 'volume';
    const collapsed = !shouldExpand(node);

    row.style.paddingLeft = `${12 + depth * 22}px`;

    const toggle = document.createElement('span');
    toggle.className = `tree-toggle${hasKids ? '' : ' leaf'}${collapsed ? ' collapsed' : ''}`;
    toggle.innerHTML = TREE_CHEVRON_SVG;
    row.appendChild(toggle);

    const name = document.createElement('span');
    name.className = 'tree-name';
    name.textContent = node.name;
    row.appendChild(name);

    const tag = document.createElement('span');
    tag.className = `tree-tag ${node.type || ''}`;
    tag.textContent = TREE_TYPE_LABEL[node.type] || node.type;
    row.appendChild(tag);

    if (node.type === 'volume' && node.volume) {
      const dot = document.createElement('span');
      dot.className = 'tree-statusdot';
      dot.style.background = palette.color(node.volume[palette.field]);
      dot.title = palette.label(node.volume[palette.field]);
      row.appendChild(dot);
    }

    const spacer = document.createElement('span');
    spacer.className = 'tree-spacer';
    row.appendChild(spacer);

    if (node.type !== 'volume' && (node.count || 0) > 0) {
      const bar = buildStatusBar(node);
      if (bar) row.appendChild(bar);
    }

    const meta = document.createElement('div');
    meta.className = 'tree-meta';
    if (node.type === 'volume' && node.volume) {
      const size = document.createElement('span');
      size.className = 'tree-meta-primary';
      size.textContent = formatBytes(node.value || 0);
      meta.appendChild(size);
      if (node.volume.diskType) {
        const ext = document.createElement('span');
        ext.className = 'tree-meta-secondary';
        ext.textContent = node.volume.diskType.toUpperCase();
        meta.appendChild(ext);
      }
    } else {
      const cnt = document.createElement('span');
      cnt.className = 'tree-meta-primary';
      cnt.textContent = `${(node.count || 0).toLocaleString()} ${node.count === 1 ? 'vol' : 'vols'}`;
      meta.appendChild(cnt);
      const stor = document.createElement('span');
      stor.className = 'tree-meta-secondary';
      stor.textContent = formatBytes(node.value || 0);
      meta.appendChild(stor);
    }
    row.appendChild(meta);
    wrap.appendChild(row);

    let kidsEl: HTMLDivElement | null = null;
    if (hasKids && node.children) {
      kidsEl = document.createElement('div');
      kidsEl.className = `tree-children${collapsed ? ' collapsed' : ''}`;
      kidsEl.style.setProperty('--line-x', `${12 + depth * 22 + 8}px`);
      let populated = false;
      const populate = () => {
        if (populated) return;
        node.children!.forEach((c) => renderNode(c, depth + 1, kidsEl!));
        populated = true;
      };
      if (!collapsed) populate();
      wrap.appendChild(kidsEl);

      const flip = (e: Event) => {
        e.stopPropagation();
        const isCollapsed = kidsEl!.classList.toggle('collapsed');
        toggle.classList.toggle('collapsed', isCollapsed);
        if (!isCollapsed) populate();
      };
      addListener(toggle, 'click', flip);
    }

    const rowClick = (e: Event) => {
      if (consumeIfPanelOutsideCloseGesture(e)) {
        return;
      }
      if (toggle.contains(e.target as Node)) return;
      e.stopPropagation();
      container.querySelectorAll('.tree-row.selected').forEach((r) => r.classList.remove('selected'));
      row.classList.add('selected');
      if (node.type === 'volume' && node.volume) {
        options.onSelectVolume(node.volume);
        return;
      }
      if (node.type === 'instance') {
        options.onSelectInstance(node.name, collectVolumes(node));
        return;
      }
      if (hasKids && kidsEl) {
        const isCollapsed = kidsEl.classList.toggle('collapsed');
        toggle.classList.toggle('collapsed', isCollapsed);
        if (!isCollapsed && kidsEl.children.length === 0) {
          node.children!.forEach((c) => renderNode(c, depth + 1, kidsEl!));
        }
      }
    };
    addListener(row, 'click', rowClick);

    parentEl.appendChild(wrap);
  }

  tree.children?.forEach((c) => renderNode(c, 0, container));

  return () => {
    listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    container.innerHTML = '';
    container.classList.remove('tree-canvas');
  };
}
